import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, Text, StyleSheet, Pressable,
} from 'react-native';
import { shallowEqual, useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import isMobile from '../../../components/IsMobile';
import updateTicketPriority from '../../../requests/Tickets/UpdateTicketPriority';
import SelectionModal from '../../../components/SelectionModal';
import getPriorityColor from '../../../components/PriorityColorScheme';
import Style from '../../../constants/Style';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import DropdownIcon12 from '../../../components/12pxIcons/DropdownIcon12';
import TickIcon16 from '../../../components/16pxIcons/TickIcon16';
import { TicketPriority, UserPermission } from '../../../constants/Types';
import { Building } from '../../LocationManagement/components/AddBuildingModal';
import {
  BuildingSettingsPriority,
} from '../../LocationManagement/components/BuildingSettingsModal/data';
import PriorityLabel from '../../LocationManagement/components/PriorityLabel';

interface TicketPriorityProps {
  id: string,
  priority: TicketPriority,
  showPriorityDropdown: boolean,
  setShowPriorityDropdown: Dispatch<SetStateAction<boolean>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  updateSelectedTicketInList: () => void,
}

const TicketPrioritySelector = ({
  id, priority, showPriorityDropdown, setShowPriorityDropdown, setLoading, setShowSpinner,
  updateSelectedTicketInList,
}: TicketPriorityProps) => {
  const [selectedPriorityIndex, setSelectedPriorityIndex] = useState<number>(priority ?? 0);
  const [priorityChangeLoading, setPriorityChangeLoading] = useState<boolean>(false);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE);
  const slaNames: string[] = useSelector((state: {
    selectedBuilding: Building
  }) => state.selectedBuilding
    .SlaHours?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);

  useEffect(() => setSelectedPriorityIndex(priority ?? 0), [id]);

  const apply = (priorityIndex: number) => {
    setShowSpinner(true);
    setPriorityChangeLoading(true);
    setShowPriorityDropdown(false);
    updateTicketPriority(
      id,
      priorityIndex,
    ).then(updateSelectedTicketInList).finally(() => {
      setLoading(true);
      setPriorityChangeLoading(false);
    });
  };

  const PriorityList = () => <>
    {slaNames.map((slaName: any, index: number) => {
      const disabled: boolean = index === selectedPriorityIndex;
      return <Pressable
        key={index}
        {...{ disabled }}
        style={(state: PressableState) => [
          Style.row, styles.item,
          state.hovered && Style.listItemHovered, state.pressed && Style.listItemPressed,
        ]}
        onPress={() => {
          setSelectedPriorityIndex(index);
          if (!isMobile) apply(index);
        }}
      >
        <View style={styles.itemContent}>
          {isMobile ? <PriorityLabel priority={index} /> : <View style={[
            styles.spot, { backgroundColor: getPriorityColor(index) },
          ]} />}
          <Text style={styles.name}>
            {!isMobile && `P${index + 1} - `}
            {slaName}
          </Text>
        </View>
        {disabled && <TickIcon16 />}
      </Pressable>;
    })}
  </>;

  return <>
    <PressableStyled
      onPress={() => setShowPriorityDropdown(!showPriorityDropdown)}
      disabled={!canUpdateTickets}
      maintainOpacityWhenDisabled
    >
      {isMobile ? <View style={Style.row}>
        <View style={Style.row}>
          <View style={[styles.spot, { backgroundColor: getPriorityColor(priority) }]} />
          <Text>{slaNames[priority]}</Text>
        </View>
        {canUpdateTickets && <DropdownIcon12 color={Colors.light.hereworksBlue400} />}
      </View> : <PriorityLabel
        {...{ priority }}
        dropdownIconShown={canUpdateTickets}
      />}
    </PressableStyled>
    {isMobile ? <SelectionModal
      title='Change priority'
      listContent={<PriorityList />}
      isVisible={showPriorityDropdown}
      hide={() => {
        setShowPriorityDropdown(false);
        setSelectedPriorityIndex(priority ?? 0);
      }}
      apply={() => apply(selectedPriorityIndex)}
      loading={priorityChangeLoading}
    /> : (showPriorityDropdown && <View style={styles.dropdown}>
      <View style={styles.dropdownTip} />
      <PriorityList />
    </View>)}
  </>;
};

const styles = StyleSheet.create({
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    marginHorizontal: 8,
    color: Colors.light.hereworksBlack600,
  },
  dropdown: {
    position: 'absolute',
    top: 44,
    end: 0,
    height: 200,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
  },
  dropdownTip: {
    backgroundColor: Colors.light.hereworksBlue100,
    width: 12,
    height: 12,
    borderRadius: 4,
    position: 'absolute',
    top: -6,
    end: 12,
    transform: [{ rotateZ: '45deg' }],
  },
  item: {
    padding: 10,
    borderRadius: 4,
    backgroundColor: '#FFF',
  },
  spot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginEnd: 8,
  },
});

export default TicketPrioritySelector;
